import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
  rating: number;
  image: string;
}

const EmployeeTestimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const testimonials: Testimonial[] = [
    {
      quote:
        'GaadiMatic gave our transport desk a single control tower for daily operations. Shift planning is smoother and escalations have dropped sharply.',
      author: 'Anita Rao',
      role: 'Admin Manager',
      company: 'TechVista Campus',
      rating: 5,
      image: 'AR',
    },
    {
      quote:
        'Employees now know exactly when the cab is arriving. That one change alone reduced helpdesk calls and improved trust in the service.',
      author: 'Kiran Mehta',
      role: 'HR Operations Lead',
      company: 'Accel BPO Services',
      rating: 5,
      image: 'KM',
    },
    {
      quote:
        'Vendor tracking, billing, and SLA monitoring used to sit in separate sheets. Now the entire operation is visible in one place.',
      author: 'Rahul Nair',
      role: 'Transport Head',
      company: 'NorthGrid Manufacturing',
      rating: 5,
      image: 'RN',
    },
    {
      quote:
        'The employee app makes night-shift commutes feel much safer. Live updates and trip visibility give our teams real confidence.',
      author: 'Sneha Kapoor',
      role: 'Employee Experience Lead',
      company: 'BlueOrbit Global',
      rating: 5,
      image: 'SK',
    },
  ];

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    const step = window.innerWidth >= 1024 ? 2 : 1;
    setCurrentIndex((prev) => (prev + step) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, testimonials.length]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    const step = window.innerWidth >= 1024 ? 2 : 1;
    setCurrentIndex((prev) => (prev - step + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, testimonials.length]);

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  useEffect(() => {
    if (!isHovered) {
      intervalRef.current = setInterval(nextSlide, 6000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [nextSlide, isHovered]);

  return (
    <section className="py-24 bg-navy-950 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
        <div className="absolute top-10 right-10 w-64 h-64 bg-primary-600 rounded-full blur-[100px]" />
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-accent-orange rounded-full blur-[100px]" />
      </div>

      <div className="container-main relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-heading">
            Trusted by HR, Admin, and Transport Teams
          </h2>
          <p className="text-lg text-gray-400">
            Enterprise commute operations become easier when planning, execution, and visibility live in one place.
          </p>
        </div>

        <div
          className="relative max-w-6xl mx-auto"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            onClick={prevSlide}
            className="absolute -left-4 lg:-left-16 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all hover:-translate-x-1 border border-white/10"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute -right-4 lg:-right-16 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all hover:translate-x-1 border border-white/10"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / (typeof window !== 'undefined' && window.innerWidth >= 1024 ? 2 : 1))}%)`,
              }}
            >
              {testimonials.map((testimonial, index) => (
                <div key={index} className="w-full lg:w-1/2 flex-shrink-0 p-3">
                  <div className="h-full bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-colors flex flex-col">
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                      <Quote className="w-8 h-8 text-primary-500 opacity-60" />
                    </div>

                    <blockquote className="text-lg text-white font-medium leading-relaxed mb-8 flex-grow">
                      "{testimonial.quote}"
                    </blockquote>

                    <div className="flex items-center gap-4 border-t border-white/10 pt-6 mt-auto">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-navy-800 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg border border-white/20">
                        {testimonial.image}
                      </div>
                      <div>
                        <div className="font-bold text-white">{testimonial.author}</div>
                        <div className="text-gray-400 text-sm">{testimonial.role}</div>
                        <div className="text-primary-400 text-xs">{testimonial.company}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: Math.ceil(testimonials.length / 2) }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index * 2)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  Math.floor(currentIndex / 2) === index ? 'w-8 bg-primary-500' : 'w-2 bg-white/20 hover:bg-white/40'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmployeeTestimonials;
